import axios from "axios";
import {
  REACT_APP_API,
  REACT_APP_AD_GATEWAY_URL,
  ORG_ID,
  DOMAIN_ID,
} from "../constants";
import { manageServiceError, checkAPISuccess } from "../utils/helpers";
import { requestsFor } from "./headerService";

// ad-service: /ad/GetLockoutEvents, /ad/GetLockoutBadSources
const adServiceAxios = axios.create({ baseURL: REACT_APP_API });
const adServiceRequests = requestsFor(adServiceAxios);

// ad-gateway-control: /api/v1/diagnostics/status, /api/v1/diagnostics/unlock
const gatewayAxios = axios.create({ baseURL: REACT_APP_AD_GATEWAY_URL });
const gatewayRequests = requestsFor(gatewayAxios);

const path = {
  events: "/ad/GetLockoutEvents",
  badSources: "/ad/GetLockoutBadSources",
  diagStatus: "/api/v1/diagnostics/status",
  diagUnlock: "/api/v1/diagnostics/unlock",
};

// Tenant scoping comes from the AuthNull session, never from URL params or
// user input. orgId → localStorage "OrgId"; tenantId → localStorage "DomainId"
// (DomainId is the per-tenant scope in this app). Server-side rejects if the
// requesting user lacks access to that tenant.
const tenantScope = () => ({ orgId: ORG_ID(), tenantId: DOMAIN_ID() });

export async function fetchLockoutEvents({
  payload,
  onSuccess,
  onFailure,
} = {}) {
  try {
    const body = {
      ...tenantScope(),
      principal: payload?.principal,
      sinceUnixMs: payload?.sinceUnixMs,
      limit: payload?.limit,
      // Default UI hides intermediate mfa_required rows and Kerberos
      // protocol-scaffolding noise (codes 14, 25). The "Show all events"
      // toggle inverts both flags.
      excludeIntermediate: payload?.excludeIntermediate ?? true,
      excludeBenignNoise: payload?.excludeBenignNoise ?? true,
    };
    const { data: response } = await adServiceRequests.post(path.events, body);
    if (checkAPISuccess(response)) {
      onSuccess && onSuccess(response.events || []);
    } else {
      onFailure && onFailure(response);
    }
  } catch (error) {
    manageServiceError(error, onFailure, "Failed to load lockout events");
  }
}

export async function fetchLockoutBadSources({
  payload,
  onSuccess,
  onFailure,
} = {}) {
  try {
    const body = {
      ...tenantScope(),
      principal: payload?.principal,
      sinceUnixMs: payload?.sinceUnixMs,
      limit: payload?.limit,
    };
    const { data: response } = await adServiceRequests.post(
      path.badSources,
      body
    );
    if (checkAPISuccess(response)) {
      onSuccess && onSuccess(response.sources || []);
    } else {
      onFailure && onFailure(response);
    }
  } catch (error) {
    manageServiceError(error, onFailure, "Failed to load bad sources");
  }
}

// Status endpoint is config-gated on the gateway. If it returns 503 the gateway
// has no LDAP credentials wired and the unlock button must be hidden.
export async function fetchDiagnosticsStatus({
  payload,
  onSuccess,
  onFailure,
} = {}) {
  try {
    const { data: response } = await gatewayAxios.get(
      `${path.diagStatus}?user=${encodeURIComponent(payload?.user || "")}`
    );
    onSuccess && onSuccess({ available: true, ...response });
  } catch (error) {
    if (error?.response?.status === 503) {
      onSuccess && onSuccess({ available: false });
      return;
    }
    manageServiceError(error, onFailure, "Failed to load diagnostics status");
  }
}

export async function unlockAccountViaGateway({
  payload,
  onSuccess,
  onFailure,
} = {}) {
  try {
    const { data: response } = await gatewayRequests.post(path.diagUnlock, {
      user: payload?.user,
      reason: payload?.reason,
    });
    onSuccess && onSuccess(response);
  } catch (error) {
    manageServiceError(error, onFailure, "Unlock failed");
  }
}
